import React, { useState } from 'react';
import {
  Mail,
  Lock,
  Sun,
  Moon,
  Check,
  KeyRound,
  ArrowRight,
  BookOpen,
  UserPlus,
  TrendingUp,
  Award
} from 'lucide-react';
import { demoCredentials } from '../services/api';
import { hasSupabaseConfig, supabase } from '../services/supabase';


// Reads /public/logo.png. If it isn't there yet, a text fallback is shown
// instead of a broken image so the layout never breaks.
function BrandLogo({ className, fallbackClassName }) {
  const [imgOk, setImgOk] = useState(true);

  if (!imgOk) {
    return <span className={fallbackClassName}>WiiZ</span>;
  }

  return (
    <img
      className={className}
      src="/AI_academy/logo.png"
      alt="Wiiz"
      onError={() => setImgOk(false)}
    />
  );
}

export default function PartnerLogin({
  onLogin,
  onSwitchToAdmin,
  theme,
  onToggleTheme
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async event => {
    event.preventDefault();
    setError('');

    // Demo credentials always work — same as admin login — regardless of
    // whether Supabase is configured.
    if (
      email.trim().toLowerCase() === demoCredentials.partner.email &&
      password === demoCredentials.partner.password
    ) {
      onLogin({
        role: 'partner',
        name: 'Demo Partner',
        email: email.trim(),
        partnerId: demoCredentials.partner.partnerId,
        authUserId: null
      });
      return;
    }

    // No Supabase configured, and demo creds didn't match — nothing else
    // to fall back to.
    if (!hasSupabaseConfig) {
      setError('Invalid demo partner credentials.');
      return;
    }

    // Real login: partners are created directly in the `partners` table by
    // Wiiz Admin (see Partners.jsx), not through Supabase Auth. So instead
    // of supabase.auth.signInWithPassword(), we check the email + password
    // straight against that table.
    //
    // NOTE: the `password` column is plain text (matching how the partner
    // record is created today). This is fine for an internal/demo tool, but
    // for a production-grade login this should be moved to Supabase Auth
    // (hashed passwords, sessions, etc.) instead of a raw column compare.
    setSubmitting(true);

    const { data: partner, error: fetchError } = await supabase
      .from('partners')
      .select('*')
      .eq('email', email.trim())
      .maybeSingle();

    setSubmitting(false);

    if (fetchError || !partner || partner.password !== password) {
      setError('Invalid email or password.');
      return;
    }

    if ((partner.status || '').toLowerCase() !== 'active') {
      setError('Your partner account is inactive. Contact Wiiz Admin.');
      return;
    }

    const displayName =
      partner.company_name ||
      [partner.first_name, partner.last_name].filter(Boolean).join(' ') ||
      partner.email;

    onLogin({
      role: 'partner',
      name: displayName,
      email: partner.email,
      partnerId: partner.partner_id,
      authUserId: null
    });
  };

  return (
    <div className="auth-shell">
      <div className="auth-page">
        <section className="auth-visual auth-visual-partner">
          <div className="auth-brand-line">
            <div className="auth-brand-mark">
  <span className="auth-brand-mark-fallback">W</span>
</div>
            <strong>WiiZ</strong>
            <span>Partner &amp; Certification Portal</span>
          </div>

          <div className="auth-copy">
            <span className="auth-kicker">PARTNER WORKSPACE</span>
            <h2>Learn. Register.<br />Grow together.</h2>
            <p>
              Access allocated courses and certifications, register people,
              track requests and manage certificates from one workspace.
            </p>
          </div>

          <div className="auth-feature-list">
            <AuthFeature icon={<BookOpen size={15} />} title="Allocated content" text="Access courses and certifications shared with you" />
            <AuthFeature icon={<UserPlus size={15} />} title="Register people" text="Register learners against allocated content" />
            <AuthFeature icon={<TrendingUp size={15} />} title="Track requests" text="Follow approval and certification progress" />
            <AuthFeature icon={<Award size={15} />} title="Certificates" text="View and download issued certificates" />
          </div>

          <div className="auth-trust">
            <span><Check size={11} /></span>
            <div>
              <b>Secure partner access</b>
              <small>Accounts are created and controlled by Wiiz Admin.</small>
            </div>
          </div>
        </section>

        <div className="auth-card-wrap">
          <form className="auth-card auth-card-large" onSubmit={submit}>
            <div className="auth-card-top-row">
              <button
                className="auth-card-toggle"
                type="button"
                onClick={onToggleTheme}
                aria-label="Toggle theme"
              >
                {theme === 'light' ? <Sun size={11} /> : <Moon size={11} />}
                {theme === 'light' ? 'Light' : 'Dark'}
              </button>
              <span className="auth-secure">PARTNER ACCESS</span>
            </div>

            <div className="auth-card-logo">
              <span className="auth-card-logo-inner">
                <BrandLogo fallbackClassName="auth-card-logo-fallback" />
              </span>
            </div>

            <div className="auth-switch auth-switch-tabs">
              <button type="button" onClick={onSwitchToAdmin}>Admin Login</button>
              <button className="active" type="button">Partner Login</button>
            </div>

            <span className="tag">SIGN IN</span>
            <h1>Welcome back</h1>
            <p className="auth-description">
              Enter your partner account credentials to access your workspace.
            </p>

            <label>
              Work email
              <div className="input-with-icon">
                <span><Mail size={15} /></span>
                <input
                  type="email"
                  value={email}
                  placeholder="you@company.com"
                  onChange={event => setEmail(event.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
            </label>

            <label>
              Password
              <div className="input-with-icon">
                <span><Lock size={15} /></span>
                <input
                  type="password"
                  value={password}
                  placeholder="••••••••"
                  onChange={event => setPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
            </label>


            {error && <div className="error auth-error">{error}</div>}

            <button className="primary full auth-submit" type="submit" disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
              {!submitting && <ArrowRight size={14} />}
            </button>

            <p className="auth-footer-note">
              Partner access is provided by the Wiiz administrator.
            </p>

            {/* <div className="demo-box auth-demo">
              <span className="auth-demo-icon"><KeyRound size={14} /></span>
              <div>
                <b>UI-only demo</b>
                <span>{demoCredentials.partner.email} / {demoCredentials.partner.password}</span>
              </div>
            </div> */}
          </form>
        </div>
      </div>
    </div>
  );
}

function AuthFeature({ icon, title, text }) {
  return (
    <div className="auth-feature">
      <span className="auth-feature-icon">{icon}</span>
      <div>
        <b>{title}</b>
        <small>{text}</small>
      </div>
    </div>
  );
}